import {
  Box,
  Breadcrumbs,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  IconButton,
  Link,
  Menu,
  MenuItem,
  TextField,
  Stack,
  Typography,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import FolderIcon from "@mui/icons-material/FolderOutlined";
import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolderOutlined";
import DriveFileMoveIcon from "@mui/icons-material/DriveFileMoveOutlined";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import CloseIcon from "@mui/icons-material/Close";
import ScheduleIcon from "@mui/icons-material/ScheduleOutlined";
import ReportProblemIcon from "@mui/icons-material/ReportProblemOutlined";
import { useCallback, useEffect, useRef, useState } from "react";
import type { MouseEvent } from "react";
import {
  assetInUseMessage,
  foyerApi,
  type AssetSummary,
  type MediaLayer,
  type PremiseSummary,
} from "../http/foyer";
import { useMediaScope, writeLayer } from "../context/PremiseScopeContext";
import { useOptionalAuth, userCan } from "../auth/AuthContext";
import { tokens } from "../theme";
import MediaUploadDialog, { tomorrowISO } from "./MediaUploadDialog";
import {
  ORIENTATION_LABELS,
  dimensionLabel,
  durationLabel,
  matchesMediaFilters,
  orientationOf,
  purgeDateLabel,
  purgeStateOf,
  type MediaFilter,
} from "@shared/media";

interface Props {
  /**
   * When provided, clicking an asset calls this instead of opening a preview.
   * Picker-style. The whole asset rides along with the URL because a caller
   * building a playlist item needs the type, the poster and the clip length -
   * all of which it would otherwise have to guess from the file extension.
   */
  onSelect?: (url: string, asset: AssetSummary) => void;
  // Optional max-height for scroll containment in dialog usage.
  scrollContain?: boolean;
  /**
   * Show exactly one layer instead of the scope's natural view. The picker
   * passes this for its per-layer tabs; the Content page leaves it off so a
   * location sees its own files plus the ones inherited from its customer.
   */
  layer?: MediaLayer;
}

/**
 * The filter chips, in the order they read: what it is, then what shape.
 *
 * Orientation applies to stills as much as clips - a portrait totem needs
 * portrait photos too - so the shape options are not scoped to video.
 */
const FILTER_OPTIONS: MediaFilter[] = [
  "video",
  "image",
  "portrait",
  "landscape",
  "custom",
];

const FILTER_LABELS: Record<MediaFilter, string> = {
  video: "Video",
  image: "Images",
  portrait: ORIENTATION_LABELS.portrait,
  landscape: ORIENTATION_LABELS.landscape,
  custom: ORIENTATION_LABELS.custom,
};

const LAYER_NOUN: Record<MediaLayer["kind"], string> = {
  global: "Global media",
  company: "Company",
  premise: "This location",
};

function isImage(contentType: string): boolean {
  return contentType.startsWith("image/");
}

/**
 * A clip still being normalised for panels.
 *
 * 🚨 `panel: null` is NOT encoding. It means no transcode was ever asked for —
 * every image, and every clip uploaded before renditions existed. Those are
 * perfectly selectable and play their master, which is what happens today.
 * Treating absence as "in progress" would hide the entire back catalogue
 * behind a spinner that never resolves.
 */
export function isEncoding(asset: {
  panel?: { state: string } | null;
}): boolean {
  return asset.panel?.state === "queued" || asset.panel?.state === "running";
}

/**
 * How often to re-read while a clip is encoding.
 *
 * A transcode is minutes, so this is about a progress bar rather than a race —
 * five seconds is soon enough that "it finished" feels immediate and rare
 * enough that a long encode costs a handful of requests.
 */
const ENCODING_POLL_MS = 5_000;

function isVideo(contentType: string): boolean {
  return contentType.startsWith("video/");
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

const parentOf = (p: string) => p.split("/").slice(0, -1).join("/");
const nameOf = (p: string) => p.split("/").pop() ?? p;

/** Stable string identity for a layer, so effects re-run when it really moves
 *  rather than on every render of a freshly-built object. */
function layerKey(l: MediaLayer): string {
  if (l.kind === "premise") return `premise:${l.premiseId}`;
  if (l.kind === "company") return `company:${l.companyId}`;
  return "global";
}

export default function MediaGallery({
  onSelect,
  scrollContain,
  layer: forcedLayer,
}: Props) {
  // Where this gallery sits: a location, a customer, or neither (Global media).
  const scope = useMediaScope();
  // Mirrors the `canManageMedia` gate the global-media API enforces. Kept on
  // `useOptionalAuth` (not `useCan`) so the gallery still renders when mounted
  // outside an <AuthProvider>, e.g. in isolation in a test.
  const isAdmin = userCan(useOptionalAuth()?.user ?? null, "canManageMedia");

  // The layer every WRITE goes to. A forced layer (picker tab) wins; otherwise
  // it is the innermost thing in scope.
  const target: MediaLayer = forcedLayer ?? writeLayer(scope);
  // Only a location's natural view merges an inherited layer. A picker tab is
  // one layer and nothing else, so it never asks for the inherited files.
  const ownOnly = !!forcedLayer;

  const [assets, setAssets] = useState<AssetSummary[]>([]);
  const [folders, setFolders] = useState<string[]>([]);
  const [cwd, setCwd] = useState(""); // current folder ("" = root)
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [moveAnchor, setMoveAnchor] = useState<HTMLElement | null>(null);
  const [scopeAnchor, setScopeAnchor] = useState<HTMLElement | null>(null);
  /** The bulk purge-date popover, and the date being typed into it. */
  const [purgeAnchor, setPurgeAnchor] = useState<HTMLElement | null>(null);
  const [purgeDraft, setPurgeDraft] = useState("");
  const [premises, setPremises] = useState<PremiseSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  /**
   * Kind and shape filters, in one control.
   *
   * Multi-select because the questions combine: an operator hanging a portrait
   * totem wants portrait clips, which single-select forces them to find by
   * eye. Nothing selected means everything, so the common case needs no
   * interaction. See `matchesMediaFilters` for how the two axes compose.
   */
  const [filters, setFilters] = useState<MediaFilter[]>([]);
  /**
   * Shapes recovered in the browser for files the server never measured.
   *
   * Dimensions are recorded at upload, so anything from before that lands here
   * with no shape at all - invisible to the shape filters and unlabelled. The
   * image is already being fetched to draw the tile, so measuring it costs one
   * decode and nothing on the wire. Keyed by filename.
   */
  const [measured, setMeasured] = useState<
    Record<string, { width: number; height: number }>
  >({});
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<AssetSummary | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  /**
   * A file this view merely INHERITS - visible and usable, but owned by a
   * broader layer, so it must not offer delete/move here. Inside a location
   * that means its customer's shared files; on the Global media page an admin
   * owns everything they can see.
   */
  const isInherited = (a: AssetSummary) => a.scope !== target.kind;
  const canManage = (a: AssetSummary) =>
    target.kind === "global" ? isAdmin : !isInherited(a);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [a, f] = await Promise.all([
        foyerApi.listAssets(target, { ownOnly }),
        foyerApi.listAssetFolders(target, { ownOnly }),
      ]);
      setAssets(a);
      setFolders(f);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
    // `target` is rebuilt each render; its identity is what actually varies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target.kind, layerKey(target), ownOnly]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  /**
   * Re-read while anything is still encoding, and stop the moment nothing is.
   *
   * 🚨 Without this the spinner is permanent. The list is fetched once, a
   * transcode finishes minutes later on the server, and the grid never hears
   * about it — so a clip that IS ready goes on looking busy and stays
   * unselectable until somebody reloads the page. That is a worse failure than
   * having no spinner at all, because it looks like the encode hung.
   *
   * Conditional on purpose: a library with nothing in flight makes no requests,
   * which is every library most of the time. `refresh` is deliberately out of
   * the deps — it changes identity with the scope, and including it would
   * restart the timer on every render.
   */
  const encodingCount = assets.filter(isEncoding).length;
  useEffect(() => {
    if (encodingCount === 0) return;
    const id = setInterval(() => {
      void refresh();
    }, ENCODING_POLL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [encodingCount]);

  /**
   * Measure images the server has no dimensions for.
   *
   * Only images: a clip cannot be sized without decoding it, which is the
   * uploader's job. Runs once per file, and a failure is silent - an
   * unmeasurable file simply stays unlabelled rather than blocking the grid.
   */
  useEffect(() => {
    const pending = assets.filter(
      (a) =>
        a.contentType.startsWith("image/") &&
        !a.orientation &&
        !measured[a.filename]
    );
    if (pending.length === 0) return;

    let alive = true;
    pending.forEach((a) => {
      const img = new Image();
      img.onload = () => {
        if (!alive || !img.naturalWidth || !img.naturalHeight) return;
        setMeasured((prev) =>
          prev[a.filename]
            ? prev
            : {
                ...prev,
                [a.filename]: {
                  width: img.naturalWidth,
                  height: img.naturalHeight,
                },
              }
        );
      };
      img.src = a.url;
    });
    return () => {
      alive = false;
    };
  }, [assets, measured]);

  /** An asset with any browser-recovered shape folded in. */
  const withShape = (a: AssetSummary): AssetSummary => {
    if (a.orientation || !measured[a.filename]) return a;
    const { width, height } = measured[a.filename]!;
    return { ...a, width, height, orientation: orientationOf(width, height) };
  };

  const childFolders = folders.filter((f) => parentOf(f) === cwd).sort();
  const filesInFolder = assets
    .map(withShape)
    .filter((a) => (a.folder || "") === cwd);
  const filesHere = filesInFolder.filter((a) =>
    matchesMediaFilters(a, filters)
  );
  /**
   * Files whose delete date has passed and that are still here (BE-29).
   *
   * Across the WHOLE library, not just the current folder: an unmet retention
   * obligation is not something an operator should have to go looking for one
   * folder at a time. Filters are ignored for the same reason.
   */
  const overdue = assets.filter((a) => purgeStateOf(a.purgeAt) === "overdue");
  const crumbs = cwd
    ? cwd.split("/").map((seg, i, arr) => ({
        label: seg,
        path: arr.slice(0, i + 1).join("/"),
      }))
    : [];

  function go(folder: string) {
    setCwd(folder);
    setSelected(new Set());
  }

  async function newFolder() {
    const name = window.prompt("New folder name")?.trim();
    if (!name) return;
    try {
      await foyerApi.createAssetFolder(cwd ? `${cwd}/${name}` : name, target);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  async function removeFolder(p: string) {
    if (!window.confirm(`Delete folder "${nameOf(p)}"? It must be empty.`))
      return;
    try {
      await foyerApi.deleteAssetFolder(p, target);
      await refresh();
    } catch {
      setError(`"${nameOf(p)}" isn't empty - move or delete its files first.`);
    }
  }

  /**
   * One upload, measured in the browser first.
   *
   * The dialog has already refused anything oversized or of the wrong type, so
   * by the time this runs the only failure left is the network.
   */
  async function uploadOne(args: {
    file: File;
    name: string;
    width: number | null;
    height: number | null;
    durationSeconds: number | null;
    posterDataUrl: string | null;
    purgeAt: string | null;
  }) {
    setUploading(true);
    setError(null);
    try {
      const summary = await foyerApi.uploadAsset(args.file, cwd, target, {
        name: args.name,
        width: args.width,
        height: args.height,
        durationSeconds: args.durationSeconds,
        posterDataUrl: args.posterDataUrl,
        purgeAt: args.purgeAt,
      });
      setAssets((prev) => [summary, ...prev]);
    } finally {
      setUploading(false);
    }
  }

  /**
   * The bare drag-and-drop / browse path: several files, no dialog, no meta.
   *
   * These uploads get NO PURGE DATE, deliberately and not by omission. There is
   * nowhere in this flow to make that decision, and inventing one - a house
   * default, or inheriting the last one used - would schedule a customer's file
   * for destruction on a date nobody chose. "No expiry" is the only honest
   * answer a path with no question can give. A date can be added afterwards
   * from the selection toolbar.
   */
  async function uploadFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    setUploading(true);
    setError(null);
    try {
      for (const f of Array.from(files)) {
        const summary = await foyerApi.uploadAsset(f, cwd, target);
        setAssets((prev) => [summary, ...prev]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  }

  // Re-scope the selected assets (admin only), served by the admin-only /scope
  // route - the one place that may move a file ACROSS layers. On the Global
  // media page that means pushing files down into a location; inside a
  // location it means promoting them to the whole company, or all the way up
  // to global.
  async function openScopeMenu(e: React.MouseEvent<HTMLElement>) {
    setScopeAnchor(e.currentTarget);
    if (target.kind === "global" && premises.length === 0) {
      try {
        setPremises(await foyerApi.listPremises());
      } catch {
        /* leave empty; the menu shows "no premises" */
      }
    }
  }

  async function setScopeFor(next: MediaLayer) {
    setScopeAnchor(null);
    const filenames = [...selected];
    if (!filenames.length) return;
    try {
      for (const f of filenames) await foyerApi.setAssetScope(f, next);
      setSelected(new Set());
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  /**
   * Set (or clear, with `null`) the purge date on everything selected.
   *
   * Bulk because that is the shape of the real job: a seasonal campaign is a
   * dozen files that end on the same day, and setting the same date twelve
   * times is how one of them gets missed. It is also the answer to "does a
   * purge date belong on a FOLDER?" for now - selecting a folder's contents
   * covers the case without inventing a second place a date can live and then
   * having to say which one wins.
   */
  async function setPurgeForSelection(next: string | null) {
    setPurgeAnchor(null);
    const filenames = [...selected];
    if (!filenames.length) return;
    try {
      for (const f of filenames)
        await foyerApi.setAssetPurgeAt(f, next, target);
      setSelected(new Set());
      setPurgeDraft("");
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  function toggleSel(filename: string) {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(filename)) n.delete(filename);
      else n.add(filename);
      return n;
    });
  }

  async function moveTo(folder: string) {
    setMoveAnchor(null);
    const filenames = [...selected];
    if (!filenames.length) return;
    try {
      await foyerApi.moveAssets(filenames, folder, target);
      setSelected(new Set());
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  /**
   * "Ej optimerad" -> try again. Flips the tile to the encoding spinner at
   * once, so the existing poll takes over and carries it to ready or failed.
   * On a refusal the server's state wins: re-read rather than guess.
   */
  async function retryPanel(filename: string) {
    setAssets((prev) =>
      prev.map((a) =>
        a.filename === filename && a.panel
          ? { ...a, panel: { ...a.panel, state: "queued", error: null } }
          : a
      )
    );
    try {
      await foyerApi.retryPanelTranscode(filename, target);
    } catch (err) {
      const code = (err as { code?: string }).code;
      setError(
        code === "panel_not_failed"
          ? "Klippet har redan börjat förberedas eller är redan klart."
          : `Kunde inte starta om: ${err instanceof Error ? err.message : String(err)}`
      );
      await refresh();
    }
  }

  async function deleteAsset(filename: string) {
    if (!window.confirm(`Delete "${filename}"?`)) return;
    try {
      await foyerApi.deleteAsset(filename, target);
      setAssets((prev) => prev.filter((a) => a.filename !== filename));
    } catch (err) {
      // A file that is still on a screen comes back as a 409 naming what holds
      // it. Showing the raw code ("asset_in_use") would tell the operator they
      // failed without telling them what to do about it.
      setError(
        assetInUseMessage(err) ??
          (err instanceof Error ? err.message : String(err))
      );
    }
  }

  return (
    <Stack spacing={2} data-testid="media-gallery">
      <MediaUploadDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUpload={uploadOne}
        destination={cwd || undefined}
      />
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
      >
        <Breadcrumbs aria-label="folder path">
          <Link
            component="button"
            underline={cwd ? "hover" : "none"}
            color={cwd ? "inherit" : "text.primary"}
            onClick={() => go("")}
            data-testid="crumb-root"
          >
            {LAYER_NOUN[target.kind]}
          </Link>
          {crumbs.map((c, i) => (
            <Link
              key={c.path}
              component="button"
              underline={i < crumbs.length - 1 ? "hover" : "none"}
              color={i < crumbs.length - 1 ? "inherit" : "text.primary"}
              onClick={() => go(c.path)}
            >
              {c.label}
            </Link>
          ))}
        </Breadcrumbs>
        <Stack direction="row" spacing={1}>
          {selected.size > 0 && (
            <Button
              size="small"
              startIcon={<DriveFileMoveIcon />}
              onClick={(e) => setMoveAnchor(e.currentTarget)}
              data-testid="media-move"
            >
              Move ({selected.size})
            </Button>
          )}
          {selected.size > 0 && (
            <Button
              size="small"
              startIcon={<ScheduleIcon />}
              onClick={(e) => setPurgeAnchor(e.currentTarget)}
              data-testid="media-purge-date"
            >
              Delete date ({selected.size})
            </Button>
          )}
          {isAdmin && selected.size > 0 && (
            <Button
              size="small"
              startIcon={<SwapHorizIcon />}
              onClick={openScopeMenu}
              data-testid="media-scope"
            >
              Move to…
            </Button>
          )}
          <Button
            size="small"
            startIcon={<CreateNewFolderIcon />}
            onClick={newFolder}
            data-testid="media-new-folder"
          >
            New folder
          </Button>
          <TextField
            select
            size="small"
            value={filters}
            onChange={(e) =>
              setFilters(
                // MUI hands a multiple select its value as an array already;
                // the string branch is only the autofill edge case.
                (typeof e.target.value === "string"
                  ? e.target.value.split(",")
                  : e.target.value) as MediaFilter[]
              )
            }
            sx={{ minWidth: 210 }}
            SelectProps={{
              multiple: true,
              displayEmpty: true,
              // Nothing selected is the common case and means "everything", so
              // it says so rather than showing an empty box.
              renderValue: (v) => {
                const picked = v as MediaFilter[];
                if (picked.length === 0) return "All media";
                if (picked.length === 1) return FILTER_LABELS[picked[0]!];
                return `${picked.length} filters`;
              },
            }}
            data-testid="media-shape-filter"
          >
            {FILTER_OPTIONS.map((f) => (
              <MenuItem key={f} value={f} data-testid={`media-filter-${f}`}>
                <Checkbox
                  size="small"
                  checked={filters.includes(f)}
                  sx={{ mr: 0.5, p: 0.5 }}
                />
                {FILTER_LABELS[f]}
              </MenuItem>
            ))}
          </TextField>
          {filters.length > 0 && (
            <Button size="small" onClick={() => setFilters([])}>
              Clear
            </Button>
          )}
          <Button
            startIcon={<UploadFileIcon />}
            variant="contained"
            size="small"
            onClick={() => setUploadOpen(true)}
            disabled={uploading}
            data-testid="media-upload"
          >
            {uploading ? "Uploading…" : "Upload media"}
          </Button>
        </Stack>
        <input
          ref={fileInput}
          type="file"
          hidden
          multiple
          onChange={(e) => uploadFiles(e.target.files)}
          // Signage loops are clips, not stills. Narrowed to the containers a
          // TV WebView will actually decode rather than a blanket video/*.
          accept="image/*,video/mp4,video/webm,video/quicktime,.mp4,.m4v,.webm,.mov"
          data-testid="media-file-input"
        />
      </Stack>

      {error && (
        <Typography color="error" variant="body2">
          {error}
        </Typography>
      )}

      {/*
        Files past their delete date that are still here (BE-29).

        This is the surface the whole feature turns on. When the hourly sweep
        reaches a file whose date has passed and something still displays it,
        the deletion is REFUSED - a live reference beats a date typed months
        ago, and blanking a wall is an editorial decision nobody made. That
        refusal is correct AND it is a retention promise that has not been kept,
        so it has to be visible here rather than only in a container log. A
        purge that quietly never happens is worse than none, because it looks
        discharged.

        Derived, never stored: a row surviving past its own date IS the state.
      */}
      {overdue.length > 0 && (
        <Stack
          direction="row"
          alignItems="flex-start"
          gap={1.5}
          sx={{
            border: `1px solid ${tokens.warning}`,
            borderRadius: 1,
            p: 1.5,
          }}
          data-testid="media-purge-overdue"
        >
          <ReportProblemIcon
            sx={{ color: tokens.warning, fontSize: 20, mt: "2px" }}
          />
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {overdue.length} file{overdue.length === 1 ? "" : "s"} should have
              been deleted by now
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Their delete date has passed but something still displays them, so
              they are still stored and still being served. Remove them from
              whatever uses them, or clear the date if they should be kept:{" "}
              {overdue
                .slice(0, 4)
                .map((a) => a.originalName)
                .join(", ")}
              {overdue.length > 4 ? `, and ${overdue.length - 4} more` : ""}.
            </Typography>
          </Box>
        </Stack>
      )}
      {loading && (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      )}
      {!loading && childFolders.length === 0 && filesHere.length === 0 && (
        <Typography
          color="text.secondary"
          variant="body2"
          sx={{ py: 3, textAlign: "center" }}
          data-testid="media-empty"
        >
          Nothing here yet. Use <strong>New folder</strong> or{" "}
          <strong>Add new</strong>.
        </Typography>
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
          gap: 2,
          ...(scrollContain ? { maxHeight: 480, overflowY: "auto" } : {}),
        }}
      >
        {/* Subfolders */}
        {childFolders.map((f) => (
          <Box
            key={f}
            data-testid={`folder-${f}`}
            onClick={() => go(f)}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              p: 2,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 1,
              position: "relative",
              "&:hover": { bgcolor: "action.hover" },
              "&:hover .folder-delete": { opacity: 1 },
            }}
          >
            <FolderIcon color="action" />
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              title={nameOf(f)}
            >
              {nameOf(f)}
            </Typography>
            <IconButton
              size="small"
              className="folder-delete"
              aria-label={`Delete folder ${nameOf(f)}`}
              onClick={(e) => {
                e.stopPropagation();
                removeFolder(f);
              }}
              sx={{ ml: "auto", opacity: 0, transition: "opacity 120ms" }}
              data-testid={`folder-delete-${f}`}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}

        {/* Files in this folder */}
        {filesHere.map((asset) => {
          const isSel = selected.has(asset.filename);
          return (
            <Box
              key={asset.filename}
              data-testid={`asset-${asset.filename}`}
              sx={{
                border: "2px solid",
                borderColor: isSel ? "primary.main" : "divider",
                borderRadius: 1,
                overflow: "hidden",
                cursor: "pointer",
                position: "relative",
                "&:hover .asset-delete": { opacity: 1 },
                "&:hover .asset-check": { opacity: 1 },
              }}
              /**
               * 🚨 Only PICKING waits. A clip mid-encode still opens in the
               * preview, still deletes, still moves — it is a real file with
               * real bytes. What it cannot do is get chosen for a wall, because
               * the rendition that makes it safe there does not exist yet.
               *
               * Blocking the whole tile would make an encoding clip look
               * broken rather than busy.
               */
              onClick={() => {
                if (onSelect && isEncoding(asset)) return;
                if (onSelect) onSelect(asset.url, asset);
                else setPreview(asset);
              }}
            >
              {canManage(asset) && (
                <Checkbox
                  size="small"
                  className="asset-check"
                  checked={isSel}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleSel(asset.filename);
                  }}
                  sx={{
                    position: "absolute",
                    top: 2,
                    left: 2,
                    bgcolor: "rgba(255,255,255,0.85)",
                    borderRadius: 1,
                    p: 0.25,
                    opacity: isSel ? 1 : 0,
                    transition: "opacity 120ms",
                    "&:hover": { bgcolor: "white" },
                  }}
                  data-testid={`asset-check-${asset.filename}`}
                />
              )}
              {isInherited(asset) && (
                <Chip
                  label={asset.scope === "company" ? "Company" : "Global"}
                  size="small"
                  title={
                    asset.scope === "company"
                      ? "Shared by this customer across all its locations - usable here, editable in Content with no location selected."
                      : "From the shared Global media library."
                  }
                  sx={{
                    position: "absolute",
                    top: 4,
                    left: 4,
                    height: 20,
                    fontSize: 11,
                    bgcolor: "rgba(255,255,255,0.9)",
                  }}
                  data-testid={`asset-inherited-${asset.filename}`}
                />
              )}
              <Box
                sx={{
                  // `relative` so the type badge anchors to the THUMBNAIL. The
                  // card is itself positioned (for the delete button), so
                  // without this the badge resolved against the card and sat
                  // on top of the caption.
                  position: "relative",
                  height: 120,
                  bgcolor: "rgba(0,0,0,0.04)",
                  display: "grid",
                  placeItems: "center",
                  overflow: "hidden",
                }}
              >
                {isImage(asset.contentType) ? (
                  <img
                    src={asset.url}
                    alt={asset.originalName}
                    style={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                    }}
                  />
                ) : isVideo(asset.contentType) && asset.posterUrl ? (
                  // A still captured at upload. Deterministic in every browser,
                  // and it costs neither a video decode nor the clip's bytes.
                  <img
                    src={asset.posterUrl}
                    alt={asset.originalName}
                    style={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                    }}
                  />
                ) : isVideo(asset.contentType) ? (
                  // Clips uploaded before posters were captured. The `#t=0.1`
                  // fragment asks the browser to seek, which is what nudges it
                  // into painting a frame at all - without it some browsers
                  // leave a metadata-only video blank.
                  <video
                    src={`${asset.url}#t=0.1`}
                    preload="metadata"
                    muted
                    playsInline
                    style={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                    }}
                  />
                ) : (
                  <Stack alignItems="center" gap={0.5}>
                    <InsertDriveFileIcon
                      sx={{ fontSize: 40, color: "text.secondary" }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      No preview
                    </Typography>
                  </Stack>
                )}
                {/* Encoding, and what it means. A bare spinner reads as a
                    stuck upload; the word says the upload is DONE and
                    something else is happening. */}
                {isEncoding(asset) && (
                  <Stack
                    alignItems="center"
                    justifyContent="center"
                    spacing={1}
                    data-testid={`asset-encoding-${asset.filename}`}
                    sx={{
                      position: "absolute",
                      inset: 0,
                      bgcolor: "rgba(15,23,42,0.62)",
                      color: "#fff",
                    }}
                  >
                    <CircularProgress size={22} sx={{ color: "#fff" }} />
                    <Typography sx={{ fontSize: 11, fontWeight: 600 }}>
                      Förbereder för skärm…
                    </Typography>
                  </Stack>
                )}
                {/* A rendition that failed. The clip is still usable — the
                    wall falls back to the master — so this warns rather than
                    blocks. Silently allowing it would be worse: an operator
                    would have no idea their clip may stall on a panel. */}
                {asset.panel?.state === "failed" && (
                  <Chip
                    size="small"
                    // The chip IS the retry where the operator owns the clip.
                    // It used to be a dead end: the only second attempt was
                    // deleting the clip and uploading it again.
                    label={
                      canManage(asset)
                        ? "Ej optimerad · Försök igen"
                        : "Ej optimerad"
                    }
                    data-testid={`asset-panel-failed-${asset.filename}`}
                    title={
                      asset.panel.error ??
                      "Klippet kunde inte förberedas för skärm. Det spelas ändå, men kan hacka."
                    }
                    {...(canManage(asset)
                      ? {
                          onClick: (e: MouseEvent) => {
                            // The tile itself selects; this must not.
                            e.stopPropagation();
                            void retryPanel(asset.filename);
                          },
                          "aria-label": `Försök förbereda ${asset.originalName} för skärm igen`,
                        }
                      : {})}
                    sx={{
                      position: "absolute",
                      bottom: 6,
                      right: 6,
                      height: 20,
                      fontSize: 10,
                      fontWeight: 700,
                      bgcolor: tokens.warning,
                      color: "#fff",
                    }}
                  />
                )}
                {/* Type badge. A grid of stills and clips is unreadable
                    without it - a paused video and a photo look identical. */}
                <Chip
                  size="small"
                  label={isVideo(asset.contentType) ? "VIDEO" : "IMAGE"}
                  sx={{
                    position: "absolute",
                    bottom: 6,
                    left: 6,
                    height: 20,
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.4px",
                    bgcolor: "rgba(15,23,42,0.78)",
                    color: "#fff",
                  }}
                />
              </Box>
              <Box sx={{ p: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    fontWeight: 500,
                  }}
                  title={asset.originalName}
                >
                  {asset.originalName}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block" }}
                >
                  {/* What an operator needs at a glance differs by kind: a
                      still is judged on its pixel size, a clip on how long it
                      holds the screen. Size is the fallback for anything
                      uploaded before shapes were measured. */}
                  {[
                    isVideo(asset.contentType)
                      ? durationLabel(asset.durationSeconds)
                      : dimensionLabel(asset.width, asset.height),
                    formatBytes(asset.size),
                  ]
                    .filter(Boolean)
                    .join("  ·  ")}
                </Typography>
                {asset.orientation && (
                  <Typography
                    variant="caption"
                    sx={{ display: "block", color: tokens.brand }}
                  >
                    {ORIENTATION_LABELS[asset.orientation]}
                  </Typography>
                )}
                {/* The delete date, on the tile rather than behind a click: a
                    file that disappears on a date is not a detail, and the
                    overdue case has to be findable in the grid it is sitting
                    in, not only in the banner that counts it. */}
                {/* Why it failed, readable without hovering. "The OSC token
                    service answered 403" tells an operator to call someone
                    rather than click again - but only if they can read it. */}
                {asset.panel?.state === "failed" && asset.panel.error && (
                  <Typography
                    variant="caption"
                    data-testid={`asset-panel-error-${asset.filename}`}
                    title={asset.panel.error}
                    sx={{
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      color: tokens.warning,
                    }}
                  >
                    {asset.panel.error}
                  </Typography>
                )}
                {purgeStateOf(asset.purgeAt) === "scheduled" && (
                  <Typography
                    variant="caption"
                    sx={{ display: "block", color: "text.secondary" }}
                    data-testid={`asset-purge-${asset.filename}`}
                  >
                    Deletes {purgeDateLabel(asset.purgeAt)}
                  </Typography>
                )}
                {purgeStateOf(asset.purgeAt) === "overdue" && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      color: tokens.warning,
                      fontWeight: 700,
                    }}
                    title="The delete date has passed but something still displays this file, so it could not be removed."
                    data-testid={`asset-purge-${asset.filename}`}
                  >
                    Delete overdue ({purgeDateLabel(asset.purgeAt)})
                  </Typography>
                )}
              </Box>
              {canManage(asset) && (
                <IconButton
                  size="small"
                  className="asset-delete"
                  aria-label={`Delete ${asset.filename}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteAsset(asset.filename);
                  }}
                  sx={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    bgcolor: "rgba(255,255,255,0.85)",
                    opacity: 0,
                    transition: "opacity 120ms",
                    "&:hover": { bgcolor: "white" },
                  }}
                  data-testid={`asset-delete-${asset.filename}`}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          );
        })}
      </Box>

      {/* Move-to menu */}
      <Menu
        anchorEl={moveAnchor}
        open={!!moveAnchor}
        onClose={() => setMoveAnchor(null)}
      >
        <MenuItem disabled dense>
          Move {selected.size} to…
        </MenuItem>
        {cwd !== "" && (
          <MenuItem onClick={() => moveTo("")} data-testid="move-to-root">
            {LAYER_NOUN[target.kind]} (root)
          </MenuItem>
        )}
        {folders
          .filter((f) => f !== cwd)
          .map((f) => (
            <MenuItem key={f} onClick={() => moveTo(f)}>
              {f}
            </MenuItem>
          ))}
      </Menu>

      {/* Automatic delete date for the selection (BE-29). A date, and a way to
          take it away again - the second is what makes the first safe to use,
          since a purge date has no grace period once it passes. */}
      <Menu
        anchorEl={purgeAnchor}
        open={!!purgeAnchor}
        onClose={() => setPurgeAnchor(null)}
      >
        <MenuItem disabled dense>
          Delete {selected.size} automatically on…
        </MenuItem>
        <Box sx={{ px: 2, py: 1 }} onKeyDown={(e) => e.stopPropagation()}>
          <TextField
            size="small"
            type="date"
            fullWidth
            autoFocus
            value={purgeDraft}
            onChange={(e) => setPurgeDraft(e.target.value)}
            inputProps={{ min: tomorrowISO() }}
            data-testid="purge-date-input"
          />
          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
            <Button
              size="small"
              variant="contained"
              disabled={!purgeDraft}
              onClick={() => setPurgeForSelection(purgeDraft)}
              data-testid="purge-date-apply"
            >
              Set date
            </Button>
            <Button
              size="small"
              onClick={() => setPurgeForSelection(null)}
              data-testid="purge-date-clear"
            >
              Never delete
            </Button>
          </Stack>
        </Box>
      </Menu>

      {/* Move-between-layers menu (admin only). Offers the layers that are
          actually reachable from here: everywhere can go global; a location
          can promote to its own customer; the Global media page can push a
          file down into a specific location. */}
      <Menu
        anchorEl={scopeAnchor}
        open={!!scopeAnchor}
        onClose={() => setScopeAnchor(null)}
      >
        <MenuItem disabled dense>
          Move {selected.size} to…
        </MenuItem>

        {target.kind !== "global" && (
          <MenuItem
            onClick={() => setScopeFor({ kind: "global" })}
            data-testid="scope-to-global"
          >
            Global media
          </MenuItem>
        )}

        {scope.companyId && target.kind !== "company" && (
          <MenuItem
            onClick={() =>
              setScopeFor({ kind: "company", companyId: scope.companyId! })
            }
            data-testid="scope-to-company"
          >
            This customer (all its locations)
          </MenuItem>
        )}

        {target.kind === "global" && [
          <MenuItem disabled dense key="_hdr">
            A single location…
          </MenuItem>,
          ...(premises.length === 0
            ? [
                <MenuItem disabled dense key="_none">
                  No locations
                </MenuItem>,
              ]
            : premises.map((p) => (
                <MenuItem
                  key={p.premiseId}
                  onClick={() =>
                    setScopeFor({ kind: "premise", premiseId: p.premiseId })
                  }
                  data-testid={`scope-to-${p.premiseId}`}
                >
                  {p.premiseName}
                </MenuItem>
              ))),
        ]}
      </Menu>

      <Dialog
        open={!!preview}
        onClose={() => setPreview(null)}
        maxWidth="lg"
        data-testid="asset-lightbox"
      >
        {preview && (
          <Box
            sx={{
              position: "relative",
              bgcolor: "#000",
              display: "grid",
              placeItems: "center",
              minWidth: 320,
              minHeight: 240,
            }}
          >
            <IconButton
              onClick={() => setPreview(null)}
              aria-label="Close preview"
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                bgcolor: "rgba(0,0,0,0.5)",
                color: "white",
                "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
              }}
              data-testid="lightbox-close"
            >
              <CloseIcon />
            </IconButton>
            {isImage(preview.contentType) ? (
              <img
                src={preview.url}
                alt={preview.originalName}
                style={{
                  display: "block",
                  maxWidth: "90vw",
                  maxHeight: "85vh",
                  objectFit: "contain",
                }}
              />
            ) : isVideo(preview.contentType) ? (
              // Controls on purpose: the reason to open a signage clip is to
              // check it, which means scrubbing it.
              <video
                src={preview.url}
                controls
                autoPlay
                muted
                playsInline
                style={{
                  display: "block",
                  maxWidth: "90vw",
                  maxHeight: "85vh",
                  objectFit: "contain",
                }}
              />
            ) : (
              <Box sx={{ p: 6, color: "white", textAlign: "center" }}>
                <InsertDriveFileIcon sx={{ fontSize: 96 }} />
                <Typography sx={{ mt: 2 }}>{preview.originalName}</Typography>
              </Box>
            )}
            <Typography
              variant="caption"
              sx={{
                position: "absolute",
                bottom: 8,
                left: 12,
                color: "rgba(255,255,255,0.7)",
              }}
            >
              {[
                preview.originalName,
                formatBytes(preview.size),
                purgeStateOf(preview.purgeAt) === "overdue"
                  ? `delete overdue since ${purgeDateLabel(preview.purgeAt)}`
                  : purgeStateOf(preview.purgeAt) === "scheduled"
                    ? `deletes ${purgeDateLabel(preview.purgeAt)}`
                    : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </Typography>
          </Box>
        )}
      </Dialog>
    </Stack>
  );
}
